import { isValidElement, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { type SearchableRoute, useSearchableRoutes } from 'components/command-palette/useSearchableRoutes';
import useAuth from 'hooks/useAuth';
import { useUiDesign } from 'hooks/useUiDesign';
import type { NavItemType } from 'types/menu';
import getMenuItems from './index';
import { isMenuItemVisible, type MenuAccess } from './menuVisibility';

export interface NavigationContextItem {
    path: string;
    label: string;
    menuPath?: string[];
    detailPathTemplate?: string;
}

type FormatMessage = ReturnType<typeof useIntl>['formatMessage'];

const STATIC_MENU_PATH_PATTERN = /^\/[A-Za-z0-9_-]+(?:\/[A-Za-z0-9_-]+)*$/;
const STATIC_ROUTE_SEGMENT_PATTERN = /^[A-Za-z0-9_-]+$/;

function normalizePath(path: string): string {
    return `/${path.split('/').filter(Boolean).join('/')}`;
}

function isCanonicalDetailPath(path: string): boolean {
    const segments = normalizePath(path).slice(1).split('/');
    return (
        segments.length > 1 &&
        segments.at(-1) === ':id' &&
        segments.filter((segment) => segment.startsWith(':')).length === 1 &&
        segments.slice(0, -1).every((segment) => STATIC_ROUTE_SEGMENT_PATTERN.test(segment))
    );
}

export function addDetailPathTemplates(
    items: NavigationContextItem[],
    routes: SearchableRoute[]
): NavigationContextItem[] {
    return items.map((item) => {
        const itemPath = normalizePath(item.path);
        const candidates = routes
            .filter(
                (route) =>
                    route.isDynamic &&
                    isCanonicalDetailPath(route.path) &&
                    normalizePath(route.breadcrumbPaths.at(-1) ?? '') === itemPath
            )
            .map((route) => normalizePath(route.path));

        if (candidates.length !== 1) {
            return item;
        }

        return { ...item, detailPathTemplate: candidates[0] };
    });
}

export function mergeSearchableRoutes(
    items: NavigationContextItem[],
    routes: SearchableRoute[]
): NavigationContextItem[] {
    const existingPaths = new Set(items.map((item) => normalizePath(item.path)));
    const staticRouteItems = routes.flatMap((route) => {
        const path = normalizePath(route.path);
        if (route.isDynamic || !STATIC_MENU_PATH_PATTERN.test(path) || existingPaths.has(path)) {
            return [];
        }

        existingPaths.add(path);
        const parent = items
            .filter((item) => path.startsWith(`${normalizePath(item.path)}/`))
            .sort((left, right) => normalizePath(right.path).length - normalizePath(left.path).length)[0];
        const menuPath = parent ? [...(parent.menuPath ?? []), parent.label] : undefined;

        return [{ path, label: route.label, ...(menuPath ? { menuPath } : {}) }];
    });

    return addDetailPathTemplates([...items, ...staticRouteItems], routes);
}

function resolveMenuLabel(title: NavItemType['title'], formatMessage: FormatMessage): string | null {
    if (typeof title === 'string') {
        return title;
    }

    if (isValidElement<{ id?: string }>(title) && typeof title.props.id === 'string') {
        return formatMessage({ id: title.props.id });
    }

    return null;
}

function flattenMenuItems(
    items: NavItemType[],
    user: MenuAccess,
    formatMessage: FormatMessage,
    menuPath: string[] = []
): NavigationContextItem[] {
    return items.flatMap((item) => {
        if (!isMenuItemVisible(item, user)) {
            return [];
        }

        if (item.type === 'item') {
            const label = resolveMenuLabel(item.title, formatMessage);
            if (
                item.disabled ||
                item.external ||
                item.target === '_blank' ||
                !item.url ||
                !STATIC_MENU_PATH_PATTERN.test(item.url) ||
                !label
            ) {
                return [];
            }

            const effectiveMenuPath = menuPath.filter((parentLabel) => parentLabel !== label);

            return [
                {
                    path: item.url,
                    label,
                    ...(effectiveMenuPath.length > 0 ? { menuPath: effectiveMenuPath } : {}),
                },
            ];
        }

        if (!item.children) {
            return [];
        }

        const label = resolveMenuLabel(item.title, formatMessage);
        const childMenuPath =
            item.type === 'collapse' && !item.showChildrenOnlyOnPopper && label ? [...menuPath, label] : menuPath;

        return flattenMenuItems(item.children, user, formatMessage, childMenuPath);
    });
}

export function readNavigationContext(
    menuItems: NavItemType[],
    user: MenuAccess,
    formatMessage: FormatMessage
): NavigationContextItem[] {
    return flattenMenuItems(menuItems, user, formatMessage);
}

export function useNavigationContext(): NavigationContextItem[] {
    const { user } = useAuth();
    const intl = useIntl();
    const searchableRoutes = useSearchableRoutes();
    const { design } = useUiDesign();
    const menuItems = useMemo(() => getMenuItems(design).items, [design]);

    return useMemo(
        () => mergeSearchableRoutes(readNavigationContext(menuItems, user, intl.formatMessage), searchableRoutes),
        [intl.formatMessage, menuItems, searchableRoutes, user]
    );
}
